import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  PieChart, Pie, Cell, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, AreaChart, Area,
  RadialBarChart, RadialBar, PolarAngleAxis,
  LineChart, Line
} from "recharts";
import {
  Users, FileText, Clock, CheckCircle,
  TrendingUp, DollarSign, PieChart as PieChartIcon,
  Calendar, ArrowUpRight, ArrowDownRight,
  BarChart3, Activity, Target, Shield,
  Eye, Filter, Download,
  MoreVertical, RefreshCw, CreditCard
} from "lucide-react";
import { useGetAllLoanApplicationsQuery } from "../../services/loanApplicationApiSlice";
import { useGetAllBorrowersQuery } from "../../services/borrowerUserApiSlice";
import { useGetAllLoansQuery } from "../../services/loanApiSlice";
import {useGetAllLoanProductsQuery} from "../../services/loanProductApiSlice";

const AdminDashboard = () => {
  // API Queries
  const { data: loanApplications = [], isLoading: loadingApplications, refetch: refetchApplications } = useGetAllLoanApplicationsQuery();
  const { data: borrowers = [], isLoading: loadingBorrowers, refetch: refetchBorrowers } = useGetAllBorrowersQuery();
  const { data: loans = [], isLoading: loadingLoans, refetch: refetchLoans } = useGetAllLoansQuery();
  const {data:loanProducts = [], isLoading:loadingLoanProducts, refetch: refetchLoanProducts} = useGetAllLoanProductsQuery();

  // Helper functions
  const getStatusColor = (status) => {
    if (!status) return '#6b7280';
    switch(status.toLowerCase()) {
      case 'approved': return '#10b981';
      case 'pending': return '#f59e0b';
      case 'submitted': return '#3b82f6';
      case 'rejected': return '#ef4444';
      case 'under_review': return '#8b5cf6';
      case 'active': return '#3b82f6';
      case 'completed': return '#10b981';
      case 'defaulted': case 'overdue': return '#ef4444';
      case 'disbursed': return '#8b5cf6';
      default: return '#6b7280';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-LK').format(num || 0);
  };

  // Trend helper for month-over-month changes
  const getTrend = (metricType, monthlyData) => {
    if (!monthlyData || monthlyData.length < 2) {
      return { direction: 'neutral', text: 'Insufficient data' };
    }

    // Assume data is in chronological order (Jan, Feb, ...)
    const last = monthlyData[monthlyData.length - 1];
    const prev = monthlyData[monthlyData.length - 2];

    if (metricType === 'applications') {
      const diff = last.applications - prev.applications;
      const pct = prev.applications ? ((diff / prev.applications) * 100).toFixed(1) : 0;
      return {
        direction: diff >= 0 ? 'up' : 'down',
        text: `${diff >= 0 ? 'Increase' : 'Decrease'} ${Math.abs(pct)}% vs last month`
      };
    } else if (metricType === 'disbursed') {
      const diff = last.disbursed - prev.disbursed;
      const pct = prev.disbursed ? ((diff / prev.disbursed) * 100).toFixed(1) : 0;
      return {
        direction: diff >= 0 ? 'up' : 'down',
        text: `${diff >= 0 ? 'Increase' : 'Decrease'} ${Math.abs(pct)}% vs last month`
      };
    }
    return { direction: 'neutral', text: 'No trend data' };
  };

  // Refetch all data
  const refetchAll = () => {
    refetchApplications();
    refetchBorrowers();
    refetchLoans();
  };

  // Calculate all statistics
  const stats = useMemo(() => {
    if (loadingApplications || loadingBorrowers || loadingLoans || loadingLoanProducts) {
      return {
        pendingApprovals: 0,
        totalUsers: 0,
        totalLoans: 0,
        activeLoans: 0,
        completedLoans: 0,
        totalLoanAmount: 0,
        averageLoanAmount: 0,
        approvalRate: 0,
        defaultRate: 0,
        totalLoanProducts: 0,
        totalRequestedAmount: 0,
      };
    }

    const pendingApprovals = loanApplications.filter(app => 
      app.status?.toLowerCase() === 'pending' || 
      app.status?.toLowerCase() === 'submitted'
    ).length;

    const approvedApplications = loanApplications.filter(app => 
      app.status?.toLowerCase() === 'approved'
    ).length;

    const applicationApprovalRate = loanApplications.length > 0 
      ? (approvedApplications / loanApplications.length) * 100 
      : 0;

    const totalRequestedAmount = loanApplications.reduce((sum, app) => 
      sum + (parseFloat(app.requestedAmount) || 0), 0);

    const uniqueLoanProducts = new Set(loanApplications
      .filter(app => app.loanProductId)
      .map(app => app.loanProductId)
    ).size;

    const activeLoans = loans.filter(loan => 
      loan.status?.toLowerCase() === 'active' || 
      loan.status?.toLowerCase() === 'approved' ||
      loan.status?.toLowerCase() === 'disbursed'
    ).length;
    
    const completedLoans = loans.filter(loan => 
      loan.status?.toLowerCase() === 'completed' || 
      loan.status?.toLowerCase() === 'paid'
    ).length;

    const defaultedLoans = loans.filter(loan => 
      loan.status?.toLowerCase() === 'defaulted' || 
      loan.status?.toLowerCase() === 'overdue'
    ).length;

    const totalLoanAmount = loans.reduce((sum, loan) => 
      sum + (parseFloat(loan.principalAmount) || 0), 0);
    
    const avgLoanAmount = loans.length > 0 ? totalLoanAmount / loans.length : 0;
    const defaultRate = loans.length > 0 ? (defaultedLoans / loans.length) * 100 : 0;

    return {
      pendingApprovals,
      totalUsers: borrowers.length,
      totalLoans: loans.length,
      activeLoans,
      completedLoans,
      defaultedLoans,
      totalLoanAmount,
      averageLoanAmount: avgLoanAmount,
      approvalRate: applicationApprovalRate,
      defaultRate,
      totalLoanProducts: loanProducts.length,
      totalRequestedAmount,
    };
  }, [loanApplications, borrowers, loans, loanProducts, loadingApplications, loadingBorrowers, loadingLoans, loadingLoanProducts]);

  // Prepare chart data
  const chartData = useMemo(() => {
    // Application Status Data
    const appStatusCounts = {};
    loanApplications.forEach(app => {
      const status = app.status || 'Unknown';
      appStatusCounts[status] = (appStatusCounts[status] || 0) + 1;
    });

    const applicationStatusData = Object.entries(appStatusCounts)
      .map(([name, value]) => ({
        name,
        value,
        fill: getStatusColor(name)
      }))
      .sort((a, b) => b.value - a.value);

    // Loan Status Data
    const loanStatusCounts = {};
    loans.forEach(loan => {
      const status = loan.status || 'Unknown';
      loanStatusCounts[status] = (loanStatusCounts[status] || 0) + 1;
    });

    const loanStatusData = Object.entries(loanStatusCounts)
      .map(([name, value]) => ({
        name,
        value,
        fill: getStatusColor(name)
      }))
      .sort((a, b) => b.value - a.value);

    // Loan Product Distribution
    const loanProductCounts = {};
    loanProducts.forEach(prod => {
      const productName = prod.loanProductName || 'Unknown Product';
      loanProductCounts[productName] = (loanProductCounts[productName] || 0) + 1;
    });

    const loanProductColors = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
    const loanProductsData = Object.entries(loanProductCounts)
      .map(([name, value], index) => ({
        name,
        value,
        color: loanProductColors[index % loanProductColors.length]
      }));

    // Monthly Performance Data
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    let monthlyData = [];
    if (loans.length > 0 && loans[0].startDate) {
      const loansByMonth = {};
      loans.forEach(loan => {
        if (loan.startDate) {
          const date = new Date(loan.startDate);
          const month = date.getMonth();
          const monthName = months[month];
          
          if (!loansByMonth[monthName]) {
            loansByMonth[monthName] = { disbursed: 0, applications: 0 };
          }
          
          loansByMonth[monthName].disbursed += parseFloat(loan.principalAmount) || 0;
          loansByMonth[monthName].applications += 1;
        }
      });

      monthlyData = months.map(month => ({
        month,
        disbursed: loansByMonth[month]?.disbursed || 0,
        applications: loansByMonth[month]?.applications || 0
      }));
    } else {
      const currentMonth = new Date().getMonth();
      monthlyData = months.slice(0, currentMonth + 1).map((month, index) => {
        const baseValue = 20 + index * 5;
        const disbursed = loans.length > 0 
          ? (stats.totalLoanAmount / (currentMonth + 1)) * (index + 1) * 0.8 
          : baseValue * 1000;
        return {
          month,
          disbursed: disbursed,
          applications: baseValue * 1.5
        };
      });
    }

    // Borrower Status Distribution
    const borrowerStatusCounts = {};
    borrowers.forEach(borrower => {
      const status = borrower.status || 'Unknown';
      borrowerStatusCounts[status] = (borrowerStatusCounts[status] || 0) + 1;
    });

    const borrowerStatusData = Object.entries(borrowerStatusCounts)
      .map(([name, value]) => ({
        name,
        value,
        fill: getStatusColor(name)
      }));

    // Amount Distribution
    const amountRanges = [
      { range: '0-1k', min: 0, max: 1000 },
      { range: '1k-5k', min: 1000, max: 5000 },
      { range: '5k-10k', min: 5000, max: 10000 },
      { range: '10k-50k', min: 10000, max: 50000 },
      { range: '50k+', min: 50000, max: Infinity }
    ];

    const amountDistributionData = amountRanges.map(range => {
      const count = loans.filter(loan => {
        const amount = parseFloat(loan.principalAmount) || 0;
        return amount >= range.min && amount < range.max;
      }).length;
      return {
        range: range.range,
        count,
        fill: getStatusColor(range.range)
      };
    }).filter(item => item.count > 0);

    return {
      applicationStatusData,
      loanStatusData,
      loanProductsData,
      monthlyData,
      borrowerStatusData,
      amountDistributionData,
    };
  }, [loanApplications, loans, borrowers, stats.totalLoanAmount, loanProducts]);

  const statCards = [
    {
      title: "Pending Approvals",
      value: stats.pendingApprovals,
      change: loanApplications.length > 0 ? 
        `+${((stats.pendingApprovals / loanApplications.length) * 100).toFixed(1)}% of total` : "No data",
      trend: "up",
      icon: <Clock className="h-5 w-5" />,
      color: "bg-gradient-to-br from-amber-500 to-amber-600",
      description: "Applications awaiting review",
    },
    {
      title: "Total Users",
      value: formatNumber(stats.totalUsers),
      change: borrowers.length > 0 ? 
        `${((borrowers.filter(b => b.status?.toLowerCase() === 'active').length / borrowers.length) * 100).toFixed(1)}% active` : "No data",
      trend: "up",
      icon: <Users className="h-5 w-5" />,
      color: "bg-gradient-to-br from-blue-500 to-blue-600",
      description: "Registered borrowers",
    },
    {
      title: "Active Loans",
      value: formatNumber(stats.activeLoans),
      change: loans.length > 0 ? 
        `${((stats.activeLoans / loans.length) * 100).toFixed(1)}% of portfolio` : "No data",
      trend: "up",
      icon: <Activity className="h-5 w-5" />,
      color: "bg-gradient-to-br from-emerald-500 to-emerald-600",
      description: "Currently active loans",
    },
    {
      title: "Loan Products",
      value: formatNumber(stats.totalLoanProducts),
      change: loanApplications.length > 0 ? 
        `${((stats.totalLoanProducts / loanApplications.length) * 100).toFixed(1)}% utilization` : "No data",
      trend: "up",
      icon: <CreditCard className="h-5 w-5" />,
      color: "bg-gradient-to-br from-purple-500 to-purple-600",
      description: "Unique loan products",
    },
  ];

  const quickActions = [
    {
      title: "Manage Users",
      description: "View and manage all users",
      icon: <Users className="h-5 w-5" />,
      link: "/admin/users",
      color: "from-blue-500 to-blue-600",
    },
    {
      title: "Loan Approvals",
      description: "Review pending applications",
      icon: <CheckCircle className="h-5 w-5" />,
      link: "/admin/approvals",
      color: "from-emerald-500 to-emerald-600",
    },
    {
      title: "Loan Management",
      description: "Manage all loans",
      icon: <FileText className="h-5 w-5" />,
      link: "/admin/loans",
      color: "from-purple-500 to-purple-600",
    },
    {
      title: "Loan Payments",
      description: "View Loan Payment details ",
      icon: <BarChart3 className="h-5 w-5" />,
      link: "/admin/loanPayments",
      color: "from-amber-500 to-amber-600",
    },
  ];

  const isLoading = loadingApplications || loadingBorrowers || loadingLoans;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="animate-pulse space-y-6">
          {/* Header Skeleton */}
          <div className="flex justify-between items-center">
            <div>
              <div className="h-8 bg-gray-200 rounded-lg w-64 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-48"></div>
            </div>
            <div className="h-10 bg-gray-200 rounded-lg w-32"></div>
          </div>

          {/* Stats Grid Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div className="h-12 w-12 bg-gray-200 rounded-xl"></div>
                  <div className="h-4 bg-gray-200 rounded w-16"></div>
                </div>
                <div className="h-8 bg-gray-200 rounded-lg w-24 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-32"></div>
              </div>
            ))}
          </div>

          {/* Charts Grid Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-sm">
                <div className="h-6 bg-gray-200 rounded w-48 mb-6"></div>
                <div className="h-64 bg-gray-200 rounded-lg"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-white rounded-xl shadow-sm border border-gray-200">
              <Activity className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Smart Lend Dashboard</h1>
              <p className="text-gray-600 text-sm md:text-base">
                {loanApplications.length} applications • {loans.length} loans • {borrowers.length} users
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.location.reload()}
            className="flex items-center gap-2 px-4 py-2.5 bg-white text-gray-700 rounded-xl border border-gray-200 hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow"
          >
            <RefreshCw className="h-4 w-4" />
            <span className="font-medium">Refresh</span>
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
        {statCards.map((stat, index) => (
          <div
            key={index}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 md:p-6 transition-all duration-300 hover:shadow-md hover:-translate-y-1 group"
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`p-3 rounded-xl ${stat.color} text-white`}>
                {stat.icon}
              </div>
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${stat.trend === 'up' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                {stat.change}
              </span>
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">{stat.value}</h3>
            <p className="text-gray-600 font-medium mb-1">{stat.title}</p>
            <p className="text-sm text-gray-500">{stat.description}</p>
          </div>
        ))}
      </div>

      {/* Financial Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <DollarSign className="h-8 w-8 opacity-80" />
            <span className="text-sm font-medium opacity-90">Total Loan Value</span>
          </div>
          <h4 className="text-3xl font-bold mb-2">{formatCurrency(stats.totalLoanAmount)}</h4>
          <p className="opacity-90">Across all active loans</p>
        </div>

        <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <Target className="h-8 w-8 opacity-80" />
            <span className="text-sm font-medium opacity-90">Approval Rate</span>
          </div>
          <h4 className="text-3xl font-bold mb-2">{stats.approvalRate.toFixed(1)}%</h4>
          <p className="opacity-90">Of total applications</p>
        </div>

        <div className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <Shield className="h-8 w-8 opacity-80" />
            <span className="text-sm font-medium opacity-90">Default Rate</span>
          </div>
          <h4 className="text-3xl font-bold mb-2">{stats.defaultRate.toFixed(1)}%</h4>
          <p className="opacity-90">{stats.defaultedLoans} loans at risk</p>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Application Status */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Application Status</h3>
              <p className="text-sm text-gray-500">Distribution of {loanApplications.length} loan applications</p>
            </div>
          </div>
          <div className="h-72">
            {chartData.applicationStatusData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData.applicationStatusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    innerRadius={40}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {chartData.applicationStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => [value, 'Applications']}
                    contentStyle={{ borderRadius: '0.75rem', border: '1px solid #e5e7eb' }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">
                No application data available
              </div>
            )}
          </div>
        </div>

        {/* Loan Status Distribution */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Loan Status Distribution</h3>
              <p className="text-sm text-gray-500">Current portfolio of {loans.length} loans</p>
            </div>
          </div>
          <div className="h-72">
            {chartData.loanStatusData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData.loanStatusData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis 
                    dataKey="name" 
                    stroke="#6b7280" 
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis 
                    stroke="#6b7280" 
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip 
                    contentStyle={{ borderRadius: '0.75rem', border: '1px solid #e5e7eb' }}
                    formatter={(value) => [value, 'Loans']}
                  />
                  <Bar 
                    dataKey="value" 
                    radius={[8, 8, 0, 0]}
                    barSize={40}
                  >
                    {chartData.loanStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">
                No loan data available
              </div>
            )}
          </div>
        </div>

        {/* Monthly Performance */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Monthly Performance</h3>
              <p className="text-sm text-gray-500">Loan disbursements over time</p>
            </div>
          </div>
          <div className="h-72">
            {chartData.monthlyData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData.monthlyData}>
                  <defs>
                    <linearGradient id="colorDisbursed" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="month" 
                    stroke="#6b7280" 
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis 
                    stroke="#6b7280" 
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip 
                    contentStyle={{ borderRadius: '0.75rem', border: '1px solid #e5e7eb' }}
                    formatter={(value) => [formatCurrency(value), 'Amount']}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="disbursed" 
                    stroke="#3b82f6" 
                    fillOpacity={1} 
                    fill="url(#colorDisbursed)"
                    name="Amount Disbursed"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">
                No monthly data available
              </div>
            )}
          </div>
        </div>

        {/* Loan Product Distribution */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Loan Product Distribution</h3>
              <p className="text-sm text-gray-500">Most popular loan products</p>
            </div>
          </div>
          <div className="h-72">
            {chartData.loanProductsData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData.loanProductsData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    height={70}
                    interval={0}
                    stroke="#1F2937"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke="#6b7280"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{ borderRadius: '0.75rem', border: '1px solid #e5e7eb' }}
                    formatter={(value) => [value, 'Applications']}
                  />
                  <Bar
                    dataKey="value"
                    radius={[8, 8, 0, 0]}
                    fill="#FFA500"
                    barSize={30}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">
                No loan product data available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions & Platform Overview (redesigned) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Quick Actions</h3>
            <div className="space-y-3">
              {quickActions.map((action, index) => (
                <Link
                  key={index}
                  to={action.link}
                  className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all duration-200 group"
                >
                  <div className={`p-3 rounded-xl bg-gradient-to-r ${action.color} text-white`}>
                    {action.icon}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                      {action.title}
                    </h4>
                    <p className="text-sm text-gray-500">{action.description}</p>
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-gray-400 group-hover:text-blue-600 transition-colors transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Platform Overview - Redesigned Metric Cards */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 h-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Platform Overview</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Card: Applications */}
              <div className="bg-gradient-to-br from-white to-blue-50 rounded-xl border border-gray-200 p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="h-5 w-5 text-blue-600" />
                  <h4 className="text-sm font-medium text-gray-600">Applications</h4>
                </div>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-3xl font-bold text-gray-900">{loanApplications.length}</span>
                  <span className="text-xs text-gray-500">total</span>
                </div>
                <p className="text-xs text-gray-500 mb-3">Total number of applications</p>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-xl font-semibold text-gray-900">{formatCurrency(stats.totalRequestedAmount)}</span>
                  <span className="text-xs text-gray-500">total value</span>
                </div>
                <p className="text-xs text-gray-500 mb-3">Total value of applications</p>
                <div className="flex items-center gap-1 text-sm">
                  {getTrend('applications', chartData.monthlyData).direction === 'up' && <ArrowUpRight className="h-4 w-4 text-emerald-600" />}
                  {getTrend('applications', chartData.monthlyData).direction === 'down' && <ArrowDownRight className="h-4 w-4 text-rose-600" />}
                  <span className="text-xs text-gray-600">{getTrend('applications', chartData.monthlyData).text}</span>
                </div>
              </div>

              {/* Card: Loans */}
              <div className="bg-gradient-to-br from-white to-emerald-50 rounded-xl border border-gray-200 p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <DollarSign className="h-5 w-5 text-emerald-600" />
                  <h4 className="text-sm font-medium text-gray-600">Loans</h4>
                </div>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-3xl font-bold text-gray-900">{loans.length}</span>
                  <span className="text-xs text-gray-500">total</span>
                </div>
                <p className="text-xs text-gray-500 mb-3">Total number of loans</p>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-xl font-semibold text-gray-900">{formatCurrency(stats.totalLoanAmount)}</span>
                  <span className="text-xs text-gray-500">outstanding</span>
                </div>
                <p className="text-xs text-gray-500 mb-3">Total loan portfolio</p>
                <div className="flex items-center gap-1 text-sm">
                  {getTrend('disbursed', chartData.monthlyData).direction === 'up' && <ArrowUpRight className="h-4 w-4 text-emerald-600" />}
                  {getTrend('disbursed', chartData.monthlyData).direction === 'down' && <ArrowDownRight className="h-4 w-4 text-rose-600" />}
                  <span className="text-xs text-gray-600">{getTrend('disbursed', chartData.monthlyData).text}</span>
                </div>
              </div>

              {/* Card: Borrowers */}
              <div className="bg-gradient-to-br from-white to-purple-50 rounded-xl border border-gray-200 p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="h-5 w-5 text-purple-600" />
                  <h4 className="text-sm font-medium text-gray-600">Borrowers</h4>
                </div>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-3xl font-bold text-gray-900">{borrowers.length}</span>
                  <span className="text-xs text-gray-500">registered</span>
                </div>
                <p className="text-xs text-gray-500 mb-3">Total borrowers</p>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-xl font-semibold text-gray-900">
                    {borrowers.filter(b => b.status?.toLowerCase() === 'active').length}
                  </span>
                  <span className="text-xs text-gray-500">active</span>
                </div>
                <p className="text-xs text-gray-500 mb-3">Active borrowers</p>
                <div className="flex items-center gap-1 text-sm">
                  <span className="text-xs text-gray-500">No monthly trend</span>
                </div>
              </div>

              {/* Card: Performance */}
              <div className="bg-gradient-to-br from-white to-amber-50 rounded-xl border border-gray-200 p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <Activity className="h-5 w-5 text-amber-600" />
                  <h4 className="text-sm font-medium text-gray-600">Performance</h4>
                </div>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-3xl font-bold text-gray-900">{stats.approvalRate.toFixed(1)}%</span>
                  <span className="text-xs text-gray-500">approval</span>
                </div>
                <p className="text-xs text-gray-500 mb-3">Application approval rate</p>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-xl font-semibold text-gray-900">{stats.defaultRate.toFixed(1)}%</span>
                  <span className="text-xs text-gray-500">default</span>
                </div>
                <p className="text-xs text-gray-500 mb-3">Default rate</p>
                <div className="flex items-center gap-1 text-sm">
                  <span className="text-xs text-gray-500">Based on portfolio</span>
                </div>
              </div>
            </div>

            {/* Optional: small note about data freshness */}
            <p className="text-xs text-gray-400 mt-4 text-right">
              Updated live • {new Date().toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;